import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from '~/components/ui/button/Button';

interface CopyButtonProps {
  /** The text put on the clipboard. */
  value: string;
  /** Accessible name and tooltip, e.g. `Copy placement key`. */
  label: string;
  /** Success toast; omit when the check icon is enough. */
  toastMessage?: string;
  testId?: string;
}

const COPIED_MS = 1500;

/** An icon button that copies `value`. Safe inside a row link: it never lets the click navigate. */
export function CopyButton({ value, label, toastMessage, testId }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      toast.error('Copy is unavailable here. Select the text and copy it manually.');
      return;
    }
    if (toastMessage) toast.success(toastMessage);
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_MS);
  }

  return (
    <>
      <Button
        size="icon"
        variant="ghost"
        className="size-7"
        onClick={copy}
        ariaLabel={label}
        title={copied ? 'Copied' : label}
        testId={testId}
      >
        {copied ? (
          <Check className="size-4 text-success-600 dark:text-success-500" aria-hidden="true" />
        ) : (
          <Copy className="size-4" aria-hidden="true" />
        )}
      </Button>
      <span role="status" className="sr-only">
        {copied ? 'Copied' : ''}
      </span>
    </>
  );
}
